import React from "react";
import { DialogTitle } from "@/components/ui/dialog";
import {
  User,
  Plus,
  List,
  Link2,
  Upload,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";

interface AddInfluencerMethodChooserProps {
  onSelectExisting: () => void;
  onSelectAddByHandle: () => void;
  onSelectImportCsv: () => void;
}

export const AddInfluencerMethodChooser: React.FC<AddInfluencerMethodChooserProps> = ({
  onSelectExisting,
  onSelectAddByHandle,
  onSelectImportCsv,
}) => {
  return (
    <>
      <div className="flex items-center gap-4 text-left">
        <div className="relative w-14 h-14 rounded-full bg-[#FAF5FF] flex items-center justify-center shrink-0">
          <User className="w-7 h-7 text-[#A655F8] stroke-[1.8]" />
          <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-[#A655F8] flex items-center justify-center text-white border-2 border-white shadow-sm">
            <Plus className="w-3 h-3 stroke-[3]" />
          </div>
        </div>
        <div>
          <DialogTitle className="text-[20px] sm:text-[22px] font-bold text-slate-900 leading-tight">
            Add Influencer to Campaign
          </DialogTitle>
          <p className="text-[13px] sm:text-[14px] text-slate-500 font-normal mt-1 leading-snug">
            Choose how you'd like to add an influencer to this campaign.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-3.5 relative z-10">
        {/* Select Existing Influencer */}
        <button
          type="button"
          onClick={onSelectExisting}
          className="w-full flex items-center gap-3.5 sm:gap-4 rounded-[16px] border border-slate-200 p-4 text-left bg-white hover:border-[#A655F8]/70 hover:bg-[#FAF5FF]/30 transition-all duration-150 group"
        >
          <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
            <List className="w-5 h-5 stroke-[2]" />
          </div>
          <div className="flex-grow min-w-0">
            <h5 className="text-[15px] font-bold text-slate-900">
              Select Existing Influencer
            </h5>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Pick from influencers already in your database
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
        </button>

        {/* Add by Handle */}
        <button
          type="button"
          onClick={onSelectAddByHandle}
          className="w-full flex items-center gap-3.5 sm:gap-4 rounded-[16px] border border-slate-200 p-4 text-left bg-white hover:border-[#A655F8]/70 hover:bg-[#FAF5FF]/30 transition-all duration-150 group"
        >
          <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
            <Link2 className="w-5 h-5 stroke-[2]" />
          </div>
          <div className="flex-grow min-w-0">
            <h5 className="text-[15px] font-bold text-slate-900">
              Add by Handle
            </h5>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Paste Instagram handles or profile links
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
        </button>

        {/* Import via CSV */}
        <button
          type="button"
          onClick={onSelectImportCsv}
          className="w-full flex items-center gap-3.5 sm:gap-4 rounded-[16px] border border-slate-200 p-4 text-left bg-white hover:border-[#A655F8]/70 hover:bg-[#FAF5FF]/30 transition-all duration-150 group"
        >
          <div className="w-12 h-12 rounded-[14px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0">
            <Upload className="w-5 h-5 stroke-[2]" />
          </div>
          <div className="flex-grow min-w-0">
            <h5 className="text-[15px] font-bold text-slate-900">
              Import via CSV
            </h5>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Upload a CSV file with influencer handles
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 group-hover:text-slate-600 group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* Footer security note */}
      <div className="mt-6 pt-1 flex items-center gap-2.5 text-slate-500 text-[12px] font-normal leading-tight relative z-10">
        <ShieldCheck className="w-4 h-4 text-[#A655F8] shrink-0 stroke-[1.8]" />
        <span>
          Your data is secure and will only be used
          <br />
          for this campaign.
        </span>
      </div>

      {/* Decorative soft purple curve and sparkles in bottom right */}
      <div className="absolute -bottom-1 -right-1 w-44 h-32 pointer-events-none select-none overflow-hidden">
        <svg
          viewBox="0 0 180 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full text-[#F3E8FF]"
        >
          <path
            d="M180 20C130 35 95 75 55 105C30 125 10 135 0 140H180V20Z"
            fill="currentColor"
          />
        </svg>
        <span className="absolute bottom-5 right-7 text-[#A855F7] text-[13px] leading-none">
          ✦
        </span>
        <span className="absolute bottom-10 right-3.5 text-[#C084FC] text-[10px] leading-none">
          ✦
        </span>
      </div>
    </>
  );
};
